// src/lib/richtext/tiptapConverters.js
import { generateHTML } from '@tiptap/core'
import { richTextExtensions } from './tiptapExtensions'
import { escapeHtml } from './utils'

const EMPTY_DOC = { type: 'doc', content: [] }

/**
 * Convert TipTap JSON document to HTML
 * @param {Object} docJson - TipTap document JSON
 * @returns {string} HTML string
 */
export function toHtmlFromJson(docJson) {
  try {
    return generateHTML(docJson || EMPTY_DOC, richTextExtensions)
  } catch (error) {
    console.warn('Failed to convert TipTap JSON to HTML:', error)
    return ''
  }
}

/**
 * Convert HTML to plain text
 * @param {string} html - HTML string
 * @returns {string} Plain text
 */
export function toPlainTextFromHtml(html) {
  try {
    if (typeof document === 'undefined') return ''
    const div = document.createElement('div')
    div.innerHTML = html || ''
    return (div.textContent || '').trim()
  } catch (error) {
    console.warn('Failed to convert HTML to plain text:', error)
    return ''
  }
}

const wrapPlainText = (text) => {
  if (!text) return '<p></p>'
  return `<p>${escapeHtml(text)}</p>`
}

const textDoc = (text) => ({
  type: 'doc',
  content: text ? [{ type: 'paragraph', content: [{ type: 'text', text }] }] : [],
})

const emptyResult = () => ({
  content_json: EMPTY_DOC,
  content_html: '<p></p>',
  content_plain: '',
})

/**
 * Ensure content is in TipTap format
 * @param {Object|string} content - Content object with various possible formats
 * @returns {Object} Normalized { content_json, content_html, content_plain }
 */
export function ensureTipTapFormat(content) {
  // Bad serialization artifact: treat literal "[object Object]" as empty
  if (typeof content === 'string') {
    if (content.trim() === '[object Object]') return emptyResult()

    const isLikelyHtml = /<\/?[a-z][\s\S]*>/i.test(content)
    if (isLikelyHtml) {
      return {
        content_json: null,
        content_html: content,
        content_plain: toPlainTextFromHtml(content),
      }
    }
    return {
      content_json: textDoc(content),
      content_html: wrapPlainText(content),
      content_plain: content,
    }
  }

  if (!content) return emptyResult()

  // Canonical: TipTap JSON
  if (content.content_json && content.content_json.type === 'doc') {
    return {
      content_json: content.content_json,
      content_html: content.content_html || toHtmlFromJson(content.content_json),
      content_plain: content.content_plain || toPlainTextFromHtml(content.content_html),
    }
  }

  // HTML-only payload — let TipTap parse it on load
  const html = content.content_html || content.html
  if (html) {
    return {
      content_json: null,
      content_html: html,
      content_plain: content.content_plain || content.text || toPlainTextFromHtml(html),
    }
  }

  // Plain-text-only payload
  const text = content.content_plain || content.text
  if (text) {
    return {
      content_json: textDoc(text),
      content_html: wrapPlainText(text),
      content_plain: text,
    }
  }

  return emptyResult()
}
